import { useEffect, useState , useCallback} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Star, BadgeCheck, Store, ArrowLeft, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CATEGORY_HEROES } from "@/lib/categoryHeroes";
import BusinessLogo from "@/components/BusinessLogo";
import BannerAdRotator from "@/components/BannerAdRotator";
import { resolveAssetUrl } from "@/lib/assetUrl";

// Compact BC score line (mirrors DirectoryPage)
function BCScoreStars({ score, count }) {
  const fullStars = Math.floor(score);
  const hasHalfStar = score % 1 >= 0.5;
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={`star-${i}`}
            className={`w-3 h-3 ${
              i < fullStars
                ? "fill-tbc-gold text-tbc-gold"
                : i === fullStars && hasHalfStar
                ? "fill-tbc-gold/50 text-tbc-gold"
                : "text-gray-300"
            }`}
          />
        ))}
      </div>
      <span className="text-xs text-gray-500 font-body ml-1">
        {score > 0 ? `${score}/5` : "No ratings"} {count > 0 && `(${count})`}
      </span>
    </div>
  );
}

export default function CategoryLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const hero = CATEGORY_HEROES[slug];

  const [businesses, setBusinesses] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");

  useEffect(() => {
    if (!hero) {
      navigate("/directory", { replace: true });
    }
  }, [hero, navigate]);

    const fetchBusinesses = useCallback(async () => {
    if (!hero) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("category", hero.name);
      if (search) params.append("search", search);
      if (location) params.append("location", location);
      const [listRes, featRes] = await Promise.all([
        axios.get(`${API}/businesses?${params.toString()}`),
        axios.get(`${API}/featured/${encodeURIComponent(hero.name)}`).catch(() => ({ data: { business: null } })),
      ]);
      setBusinesses(listRes.data.businesses || []);
      setTotal(listRes.data.total ?? (listRes.data.businesses?.length || 0));
      setFeatured(featRes.data?.business || null);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Category fetch failed:", e);
      setBusinesses([]);
      setTotal(0);
      setFeatured(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBusinesses();
  };

  if (!hero) return null;

  return (
    <div className="min-h-screen bg-white" data-testid={`category-page-${slug}`}>
      <Navbar />

      {/* Hero banner — full-bleed user artwork */}
      <section className="relative w-full overflow-hidden bg-black">
        <div
          className="w-full h-[360px] sm:h-[480px] lg:h-[560px] bg-center bg-cover"
          style={{ backgroundImage: `url(${hero.image})` }}
          aria-label={`${hero.name} category banner`}
          data-testid="category-hero-banner"
        />
        {/* Subtle bottom fade so the title sits cleanly underneath */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white pointer-events-none" />
      </section>

      {/* Title strip */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link
              to="/directory"
              className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-gray-500 hover:text-tbc-navy transition-colors"
              data-testid="category-back-to-directory"
            >
              <ArrowLeft className="w-3 h-3" /> All categories
            </Link>
            <h1 className="font-heading text-4xl sm:text-5xl text-tbc-navy mt-2">
              {hero.name}
            </h1>
            {hero.tagline && (
              <p className="text-tbc-gold text-sm tracking-widest uppercase mt-2">
                {hero.tagline}
              </p>
            )}
            <p className="text-gray-600 mt-3 max-w-2xl">{hero.description}</p>
          </div>
          <div className="text-right">
            <p className="font-heading text-3xl text-tbc-navy">{total}</p>
            <p className="text-xs uppercase tracking-widest text-gray-500">
              {total === 1 ? "business" : "businesses"}
            </p>
          </div>
        </div>
        {/* Sponsored banner (Premium category targeting) */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          <BannerAdRotator placement={`category:${slug}`} />
        </div>
      </section>

      {/* Filter bar */}
      <section className="border-y border-gray-100 bg-tbc-cream/40">
        <form
          onSubmit={handleSearch}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-3"
        >
          <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-white rounded-full px-4 py-2.5 border border-gray-200">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm"
              placeholder={`Search ${hero.name}...`}
              data-testid="category-search-input"
            />
          </div>
          <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2.5 border border-gray-200">
            <MapPin className="w-4 h-4 text-gray-400" />
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="bg-transparent outline-none text-sm w-40"
              placeholder="City, State"
              data-testid="category-location-input"
            />
          </div>
          <Button
            type="submit"
            className="rounded-full bg-tbc-navy hover:bg-tbc-navy/90 text-white text-sm px-5"
            data-testid="category-search-submit"
          >
            Search
          </Button>
        </form>
      </section>

      {/* Listings */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured business (Premium perk) */}
          {featured && (
            <Link
              to={`/business/${featured.business_id}`}
              className="block mb-8 group"
              data-testid={`featured-business-${featured.business_id}`}
            >
              <div className="relative overflow-hidden rounded-2xl border border-tbc-gold/40 bg-gradient-to-r from-tbc-navy to-[#0E2541] shadow-lg">
                <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 bg-tbc-gold text-tbc-navy text-[11px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
                  <Sparkles className="w-3 h-3" />
                  Featured this week
                </div>
                <div className="grid sm:grid-cols-2 gap-0">
                  <div className="aspect-[16/10] sm:aspect-auto overflow-hidden">
                    <img
                      src={resolveAssetUrl(featured.image_url) || "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80"}
                      alt={featured.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 sm:p-8 flex flex-col justify-center text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-heading text-2xl sm:text-3xl">{featured.name}</h3>
                      {featured.is_verified && <BadgeCheck className="w-5 h-5 text-tbc-gold" />}
                    </div>
                    <p className="text-gray-300 text-sm line-clamp-3 mb-4">{featured.description}</p>
                    {featured.city && featured.state && (
                      <div className="flex items-center text-gray-400 text-xs mb-3">
                        <MapPin className="w-3 h-3 mr-1" />
                        {featured.city}, {featured.state}
                      </div>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-tbc-gold text-sm font-medium group-hover:gap-2.5 transition-all">
                      Discover this business <ArrowLeft className="w-4 h-4 rotate-180" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-100 rounded-xl h-80 animate-pulse"></div>
              ))}
            </div>
          ) : businesses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((business) => (
                <Link
                  key={business.business_id}
                  to={`/business/${business.business_id}`}
                  className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300"
                  data-testid={`category-business-${business.business_id}`}
                >
                  <div className="aspect-[4/3] relative overflow-hidden bg-tbc-cream/70 flex items-center justify-center">
                    <BusinessLogo
                      name={business.name}
                      logoUrl={business.logo_url}
                      size="2xl"
                      shape="rounded"
                    />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-xs font-medium text-tbc-navy rounded-full">
                        {business.category === "Other" && business.custom_category
                          ? business.custom_category
                          : business.category}
                      </span>
                      {business.is_verified && (
                        <span className="px-2 py-1 bg-tbc-gold text-tbc-navy text-xs font-medium rounded-full flex items-center gap-1">
                          <BadgeCheck className="w-3 h-3" />
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-xl text-tbc-navy group-hover:text-tbc-gold transition-colors">
                      {business.name}
                    </h3>
                    <div className="mt-2">
                      <BCScoreStars
                        score={business.bc_score || 0}
                        count={business.bc_score_count || 0}
                      />
                    </div>
                    <p className="text-gray-600 text-sm font-body line-clamp-2 mt-2">
                      {business.description}
                    </p>
                    {business.city && business.state && (
                      <div className="flex items-center text-gray-400 text-xs font-body mt-3">
                        <MapPin className="w-3 h-3 mr-1" />
                        {business.city}, {business.state}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20" data-testid="category-empty-state">
              <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading text-2xl text-tbc-navy mb-2">
                No {hero.name} businesses yet
              </h3>
              <p className="text-gray-500 font-body max-w-md mx-auto mb-6">
                Be the first to register and lead this category.
              </p>
              <Link to="/dashboard">
                <Button
                  className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6"
                  data-testid="category-register-cta"
                >
                  Register Your Business
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
